import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import React from 'react';
import useVariantPrice from '@modules/product/hook/use-variant-price';
import * as S from './VariantPricing.style';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { PriceUnit } from './PriceUnit';
import VariantPricingMobile from './VariantPricing.mobile';
import type { ProductPricingTypeEnum } from 'generated/graphql';
import cx from 'classnames';
import PriceUnitEnum from '@modules/product/type/price-unit-enum';

interface VariantPricingProps {
    pricingType: ProductPricingTypeEnum;
}

const formatPrice = (price: string) => price.replace(/\.00$/, '');

export const buildPriceRangeString = (
    price: string,
    maxPrice: string,
    showMaxPrice: boolean
) => {
    if (price === maxPrice || !showMaxPrice) {
        return formatPrice(price);
    }

    return `${formatPrice(price)} - ${formatPrice(maxPrice)}`;
};

const VariantPricing = ({ pricingType }: VariantPricingProps) => {
    const { selectedVariant } = useVariantSelectionState();
    const { price, isOnSale, maxPrice, retailPrice, isPriceRange } =
        useVariantPrice(pricingType);
    const { mobile } = useAppDisplayModeContext();
    const { css } = useStyles({ isPriceRange });

    const showMaxPrice =
        isPriceRange &&
        maxPrice.Amount > 0 &&
        price.Amount > 0 &&
        maxPrice.Amount !== price.Amount;

    const priceToShow = isPriceRange
        ? price.Amount > 0
            ? price
            : maxPrice
        : price;

    const showPriceUnit =
        selectedVariant &&
        selectedVariant.PriceUnit &&
        selectedVariant.PriceUnit !== PriceUnitEnum.Unit;

    if (mobile) {
        return (
            <VariantPricingMobile
                pricingType={pricingType}
                priceToShow={priceToShow}
                showMaxPrice={showMaxPrice}
            />
        );
    }

    return (
        <div className={cx(css(S.VariantPricing), 'variant-pricing')}>
            <div className={cx(css(S.PriceGroup), 'price-group')}>
                {isPriceRange && (
                    <span className={cx(css(S.PricePrefix), 'price-prefix')}>
                        {price.Amount > 0 ? 'From' : 'Up to'}
                    </span>
                )}
                {isOnSale && !isPriceRange && (
                    <del className={cx(css(S.RetailPrice), 'retail-price')}>
                        {retailPrice.ShortFormat.replace(/\.00$/, '')}
                    </del>
                )}
                <div className={cx(css(S.Price), 'price')}>
                    {buildPriceRangeString(
                        priceToShow.ShortFormat,
                        maxPrice.ShortFormat,
                        showMaxPrice
                    )}
                    {isPriceRange ? <span>*</span> : null}
                </div>
                {showPriceUnit || isPriceRange ? (
                    <div className={cx(css(S.Subtitle), 'subtitle')}>
                        {isPriceRange && <span>*Price indication</span>}
                        {selectedVariant && (
                            <>
                                {isPriceRange ? ' ' : null}
                                <PriceUnit
                                    variant={selectedVariant}
                                    isPriceRange={isPriceRange}
                                />
                            </>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default VariantPricing;
